import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { AnswerRequest, Form } from "../dynamicForm";
import type { CustomHistoryEntry, HistoryEntry } from "../dynamicHistory/types";
import type { RegistrationById, RegistrationsGridView } from "./registrations.types";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { queryClient } from "./queryClient";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/enterprise-customer/shared-processings`;

const keys = {
  all: () => ["sharedProcessings"] as const,
  byId: (id: string) => [...keys.all(), id] as const,
  history: (id: string) => [...keys.all(), id, "history"] as const,
  gridView: () => [...keys.all(), "gridview"] as const,
  form: () => [...keys.all(), "form"] as const,
  sharedOrganisations: (id: string) => [...keys.all(), id, "sharedOrganisations"] as const,
} as const;

export const sharedProcessingsGridViewQueryOptions = () =>
  queryOptions({
    queryKey: keys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/gridview`, { schema: z.custom<RegistrationsGridView>() }),
    throwOnError: true,
  });

export const sharedProcessingFormQueryOptions = () =>
  queryOptions({
    queryKey: keys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/form`, { schema: z.custom<Form>() }),
    throwOnError: true,
  });

export const sharedProcessingByIdQueryOptions = (id: string, options?: { enabled: boolean }) =>
  queryOptions({
    queryKey: keys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}`, { schema: z.custom<RegistrationById>() }),
    throwOnError: true,
    ...options,
  });

type SharedProcessingHistoryResponse = {
  history: HistoryEntry<
    | CustomHistoryEntry<"registrationName", string>
    | CustomHistoryEntry<"registrationResponsible", { id: string; firstName: string; lastName: string; email: string }>
    | CustomHistoryEntry<"sharedRegistrationAddedToOrganisation", { value: string; name: string } | null>
  >[];
};

export const sharedProcessingHistoryQueryOptions = (id: string) =>
  queryOptions({
    queryKey: keys.history(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}/history`, { schema: z.custom<SharedProcessingHistoryResponse>() }),
    throwOnError: true,
  });

export const createSharedProcessingMutationOptions = () =>
  mutationOptions({
    mutationFn: (payload: { name: string }) =>
      api.post(BASE_ENDPOINT, payload, { schema: z.object({ id: z.string() }) }),
    onSuccess: () => invalidateAll(),
  });

export const updateSharedProcessingMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, ...payload }: { id: string; name: string }) => api.patch(`${BASE_ENDPOINT}/${id}`, payload),
    onSuccess: () => invalidateAll(),
  });

export const copySharedProcessingMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, ...payload }: { id: string; name: string }) =>
      api.post(`${BASE_ENDPOINT}/${id}/copy`, payload, { schema: z.object({ id: z.string() }) }),
    onSuccess: () => invalidateAll(),
  });

export const deleteSharedProcessingMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.delete(`${BASE_ENDPOINT}/${id}`),
    onSuccess: (_, id) => invalidateAll({ ignore: [keys.byId(id)] }),
  });

export const setSharedProcessingResponsibleMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.post(`${BASE_ENDPOINT}/${id}/responsible/${userId}`),
    onSuccess: () => invalidateAll(),
  });

export const sharedProcessingSharedOrganisationsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: keys.sharedOrganisations(id),
    queryFn: () =>
      api.get(`${BASE_ENDPOINT}/${id}/shared-organisations`, {
        schema: z.array(z.object({ organisationId: z.string(), organisationName: z.string(), isShared: z.boolean() })),
      }),
    throwOnError: true,
  });

export const shareSharedProcessingMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, organisationIds }: { id: string; organisationIds: Array<string> }) =>
      api.post(`${BASE_ENDPOINT}/${id}/share`, { organisationIds }),
    onSuccess: () => invalidateAll(),
  });

const updateSharedProcessingCache = (updated: RegistrationById) => {
  queryClient.setQueryData(keys.byId(updated.id), updated);
  queryClient.setQueryData(keys.gridView(), (gridView: RegistrationsGridView | undefined) => {
    if (!gridView) return undefined;
    return {
      ...gridView,
      registrations: gridView.registrations.map(row =>
        row.registrationId === updated.id ? { ...row, ...updated } : row
      ),
    } satisfies RegistrationsGridView;
  });
};

export const updateSharedProcessingAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ id, answers }: { id: string; answers: Array<AnswerRequest> }) =>
      api.patch(`${BASE_ENDPOINT}/${id}/answers`, answers, { schema: z.custom<RegistrationById>() }),
    onSuccess: updateSharedProcessingCache,
  });

export const updateSharedProcessingsGridViewFiltersMutationOptions = () =>
  mutationOptions({
    mutationFn: (stringifiedFilters: string) => api.put(`${BASE_ENDPOINT}/gridview/filters`, { stringifiedFilters }),
    onMutate: (stringifiedFilters: string) => {
      queryClient.setQueryData(keys.gridView(), (gridView: RegistrationsGridView | undefined) => {
        if (!gridView) return undefined;
        return { ...gridView, stringifiedFilters } satisfies RegistrationsGridView;
      });
    },
  });
